/* eslint-disable max-len */
import {QueryKey} from '@tanstack/react-query';
import {every, includes, isEmpty, isUndefined, overEvery, size, some, values} from 'lodash';
import {FormattedNumber} from 'apstra-ui-common';
import type {JSONSchema7TypeName} from 'json-schema';

import {discreteStateColors} from '../graphColors';
import AnomalousMetrics from './components/AnomalousMetrics';
import TrafficSearchBox, {requestInterfacesTraffic, requestInterfacesTrafficKey} from './components/TrafficSearchBox';
import {OPTICAL_PROBE_METRICS} from './consts';
import makePrecedingStageColumnDefinition from './makePrecedingStageColumnDefinition';
import {convertValueType} from './stageUtils';
import {
  Probe, ProbeProcessor, ProbeStage, ProcessorType, RenderingStrategy, StageDataSource
} from './types';
import {AnomalousOpticalMetric, DeviceTraffic, DiscreteStateTimelineWithLegend,
  GaugeCount, GaugeCountWithRange, GaugePercent, GaugePercentWithRange,
  LineChartWithRange, MissingMacCount, PrimitiveValue, DiscreteStateTimelineWithSamples,
  RoutesStatus} from './components/valueRenderers';

export type ProbePatternMatcherContext = {
  dataSource: StageDataSource,
  probe: Probe,
  processor: ProbeProcessor,
  stage: ProbeStage
};

export type PatternFetchDataContext = {
  blueprintId: string,
  filters: object,
  probe: Probe,
  processor: ProbeProcessor,
  routes: Record<string, string>,
  stage: ProbeStage,
}

export type ProbePatternSchema = {
  description: string,
  graph: Array<StageMatcherSchema & {
    options?: StageMatcherOptions,
  }>,
  renderingStrategy: RenderingStrategy,
  dataSource: StageDataSource,
} & (
  | {
      fetchData: (context: PatternFetchDataContext & {signal: AbortSignal}) => Promise<any>;
      fetchDataKey: (context: PatternFetchDataContext) => QueryKey
    }
  | {
      fetchData?: undefined,
      fetchDataKey?: undefined
    }
);

export type StageMatcherSchema = {
  inputName?: string,
  outputName?: string,
  processorType?: ProcessorType,
  isMetricLoggingEnabled?: boolean,
  values?: Record<string, {
    type: JSONSchema7TypeName,
    hasPossibleValues?: boolean,
  }>,
}

export type StageMatcherOptions = {
  fetchStageData?: boolean,
  fetchStageDataForOutputs?: string[],
  doNotUseAggregation?: boolean,
}

const accumulateExtraColumnFormatter = ({value}) => <FormattedNumber value={value} />;
const makeAccumulateExtraColumnDefinition = (types, columnDefinition) => ({stage}) => (
  size(stage.values) === 1 && stage.values.value?.type && types.includes(convertValueType(stage.values.value.type))
) ? columnDefinition : null;
const accumulateExtraColumns = [
  makeAccumulateExtraColumnDefinition(['string', 'number'], {
    name: 'count',
    label: 'Count',
    value: ['count'],
    formatter: accumulateExtraColumnFormatter,
    sortable: true,
    type: 'integer',
  }),
  makeAccumulateExtraColumnDefinition(['number'], {
    name: 'sum',
    label: 'Sum',
    value: ['sum'],
    formatter: accumulateExtraColumnFormatter,
    sortable: true,
    type: 'float',
  }),
  makeAccumulateExtraColumnDefinition(['number'], {
    name: 'avg',
    label: 'Average',
    value: ['avg'],
    formatter: accumulateExtraColumnFormatter,
    sortable: true,
    type: 'float',
  }),
  makeAccumulateExtraColumnDefinition(['number'], {
    name: 'std_dev',
    label: 'Standard Deviation',
    value: ['std_dev'],
    formatter: accumulateExtraColumnFormatter,
    sortable: true,
    type: 'float',
  }),
];

const opticalTransceiversExtraColumns = [
  makePrecedingStageColumnDefinition('media_type', 0),
  makePrecedingStageColumnDefinition('vendor_sn', 0),
  // FIXME(vkramskikh): instead of hardcoding stageGraphColors, they need to be derived from the graph,
  // utilizing the same logic used in StageData.stageGraphColors
  makePrecedingStageColumnDefinition('fiber_type', 0, {stageGraphColors: discreteStateColors}),
  makePrecedingStageColumnDefinition('vendor', 0),
  makePrecedingStageColumnDefinition('part_number', 0),
  AnomalousMetrics.columnDefinition,
];

export const probePatternSchemas: ProbePatternSchema[] = [
  // [N] {TS} → Range Check
  {
    description: 'Shows outliers of a time series numeric metric.',
    dataSource: 'real_time',
    graph: [
      {
        values: {value: {type: 'number'}},
        isMetricLoggingEnabled: true,
        options: {
          fetchStageData: true
        }
      },
      {processorType: 'range_check'},
    ],
    renderingStrategy: {
      ValueRenderer: LineChartWithRange,
      usePatternDescriptionAsValueDescription: true,
      noAnomalyHighlighting: true
    },
  },
  // [N] → Range Check {TS} → Time in State {TS}
  {
    description: 'Shows current state and history of sustained outliers of an instantaneous numeric metric.',
    dataSource: 'real_time',
    graph: [
      {
        values: {value: {type: 'number'}},
        isMetricLoggingEnabled: false,
        options: {
          fetchStageData: true
        }
      },
      {
        processorType: 'range_check',
        options: {
          fetchStageData: true
        },
      },
      {
        processorType: 'time_in_state_check',
        values: {value: {type: 'string', hasPossibleValues: true}},
        isMetricLoggingEnabled: true,
        options: {
          doNotUseAggregation: true,
          fetchStageData: true,
        }
      },
    ],
    renderingStrategy: {
      ValueRenderer: DiscreteStateTimelineWithLegend,
      usePatternDescriptionAsValueDescription: true,
      noAnomalyHighlighting: true
    },
  },
  // [N] {TS} → Range Check {TS} → Time in State {TS}
  {
    description: 'Shows current state and history of sustained outliers of a time series numeric metric.',
    dataSource: 'real_time',
    graph: [
      {
        values: {value: {type: 'number'}},
        isMetricLoggingEnabled: true,
        options: {
          fetchStageData: true
        }
      },
      {
        processorType: 'range_check',
        options: {
          fetchStageData: true
        },
      },
      {
        processorType: 'time_in_state_check',
        isMetricLoggingEnabled: true,
        options: {
          doNotUseAggregation: true,
          fetchStageData: true,
        },
      },
    ],
    renderingStrategy: {
      ValueRenderer: DiscreteStateTimelineWithSamples,
      usePatternDescriptionAsValueDescription: true,
      noAnomalyHighlighting: true
    },
  },
  // [DS] → Match Count
  {
    description: 'For each group of series show the number of series which instantaneous values are inside/outside of expected ranges.',
    dataSource: 'real_time',
    graph: [
      {values: {'*': {type: 'string', hasPossibleValues: true}}},
      {processorType: 'match_count'},
    ],
    renderingStrategy: {
      getRenderer: ({name}) => name === 'value' ? GaugeCount : PrimitiveValue,
      includePrecedingProcessorAnomalies: true,
    },
  },
  // [DS] → Match Count → Range Check
  {
    description: 'For each group of series calculate how many series have anomalous values. If this number exceeds a threshold, the whole group is considered anomalous.',
    dataSource: 'real_time',
    graph: [
      {values: {'*': {type: 'string', hasPossibleValues: true}}},
      {
        processorType: 'match_count',
        options: {
          fetchStageData: true
        },
      },
      {processorType: 'range_check'},
    ],
    renderingStrategy: {
      ValueRenderer: GaugeCountWithRange,
      usePatternDescriptionAsValueDescription: true,
    },
  },
  // [DS] → Match Percentage
  {
    description: 'For each group of series show the number of series which instantaneous values are inside/outside of expected ranges.',
    dataSource: 'real_time',
    graph: [
      {values: {'*': {type: 'string', hasPossibleValues: true}}},
      {processorType: 'match_perc'},
    ],
    renderingStrategy: {
      getRenderer: ({name}) => name === 'value' ? GaugePercent : PrimitiveValue,
      usePatternDescriptionAsValueDescription: true,
      includePrecedingProcessorAnomalies: true,
    },
  },
  // [DS] → Match Percentage → Range Check
  {
    description: 'For each group of series calculate how many series have anomalous values. If this number exceeds a threshold, the whole group is considered anomalous.',
    dataSource: 'real_time',
    graph: [
      {values: {'*': {type: 'string', hasPossibleValues: true}}},
      {
        processorType: 'match_perc',
        options: {
          fetchStageData: true
        },
      },
      {processorType: 'range_check'},
    ],
    renderingStrategy: {
      ValueRenderer: GaugePercentWithRange,
      usePatternDescriptionAsValueDescription: true,
    },
  },
  {
    description: 'Utilization Path',
    dataSource: 'real_time',
    graph: [
      {processorType: 'traffic_monitor'},
      {processorType: 'system_utilization', inputName: 'rx_bps'},
    ],
    renderingStrategy: {
      StageDataRenderer: DeviceTraffic,
      noPagination: true,
      noCustomization: true,
      SearchComponent: TrafficSearchBox,
      filterTransformer: TrafficSearchBox.filterTransformer,
      filterSerializer: TrafficSearchBox.filterSerializer,
      filterDeserializer: TrafficSearchBox.filterDeserializer,
      clearFilterOnContextTrigger: true,
      shouldFetchData: TrafficSearchBox.shouldFetchData,
      hiddenContextByDefault: true,
      noDataMessage: 'Select Source and Destination systems to visualize traffic between them',
    },
    fetchData: requestInterfacesTraffic,
    fetchDataKey: requestInterfacesTrafficKey,
  },
  {
    description: 'Accumulate: extra columns (always on)',
    dataSource: 'real_time',
    graph: [
      {processorType: 'accumulate', outputName: 'out'},
    ],
    renderingStrategy: {
      extraColumns: accumulateExtraColumns,
      alwaysUseContext: true,
    },
  },
  {
    description: 'Routes: custom route status renderer and default filter to hide unexpected routes (always on)',
    dataSource: 'real_time',
    graph: [
      {outputName: 'routes'},
    ],
    renderingStrategy: {
      defaultFilter: 'value != "Unexpected"',
      ValueRenderer: RoutesStatus,
      alwaysUseContext: true,
    },
  },
  {
    description: 'MAC Monitor: Missing MAC Counts stage link (always on)',
    dataSource: 'real_time',
    graph: [
      {processorType: 'mac', outputName: 'missing'},
    ],
    renderingStrategy: {
      ValueRenderer: MissingMacCount,
      alwaysUseContext: true,
      noSpotlightMode: true,
    },
  },
  {
    description: 'Optical Transceivers: Interface Stats & Lane Stats (always on)',
    dataSource: 'time_series',
    graph: [
      {
        processorType: 'optical_xcvr',
        options: {
          fetchStageDataForOutputs: ['interface'],
        }
      }
    ],
    renderingStrategy: {
      getRenderer: ({name}) => includes(OPTICAL_PROBE_METRICS, name) ? AnomalousOpticalMetric : null,
      graphCombiningAvailable: ({name}: any = {}) => !includes(OPTICAL_PROBE_METRICS, name),
      alwaysUseContext: true,
      noSpotlightMode: false,
    },
  },
  {
    description: 'Optical Transceivers: Anomalous Metrics for Summarized Interface Status (always on)',
    dataSource: 'real_time',
    graph: [
      {
        processorType: 'optical_xcvr',
        options: {
          fetchStageDataForOutputs: ['interface', 'lane']
        }
      },
      {processorType: 'optical_threshold', inputName: 'rx_power_has_high_alarm'},
    ],
    renderingStrategy: {
      extraColumns: opticalTransceiversExtraColumns,
      alwaysUseContext: true,
    },
  },
  {
    description: 'Optical Transceivers: Anomalous Metrics for Sustained Optical Threshold Anomaly (always on)',
    dataSource: 'real_time',
    graph: [
      {
        processorType: 'optical_xcvr',
        options: {
          fetchStageDataForOutputs: ['interface', 'lane']
        }
      },
      {processorType: 'optical_threshold', inputName: 'rx_power_has_high_alarm'},
      {processorType: 'time_in_state_check'},
    ],
    renderingStrategy: {
      extraColumns: opticalTransceiversExtraColumns,
      alwaysUseContext: true,
    },
  },
  {
    description: 'Environment Processor: Power Supply Count - default aggregation "last" (always on)',
    dataSource: 'real_time',
    graph: [
      {
        values: {
          operational_power_supply_count: {type: 'number'},
          total_power_supply_count: {type: 'number'},
        }
      },
    ],
    renderingStrategy: {
      aggregationType: 'last',
      alwaysUseContext: true,
    },
  },
  {
    description: 'Environment Processor: Total Fan Tray Count - default aggregation "last" (always on)',
    dataSource: 'real_time',
    graph: [
      {values: {total_fan_tray_count: {type: 'number'}}},
    ],
    renderingStrategy: {
      aggregationType: 'last',
      alwaysUseContext: true,
    },
  },
];

type MatcherFunction = (arg: {stageMatcherSchema: StageMatcherSchema, context: ProbePatternMatcherContext}) => boolean;

const matchers: Record<string, MatcherFunction> = {
  checkEnabledMetricLogging: ({stageMatcherSchema, context: {stage}}) =>
    isUndefined(stageMatcherSchema.isMetricLoggingEnabled) ||
    stageMatcherSchema.isMetricLoggingEnabled === stage.enable_metric_logging,

  checkOutputName: ({stageMatcherSchema, context: {processor, stage}}) =>
    isUndefined(stageMatcherSchema.outputName) ||
    processor.outputs[stageMatcherSchema.outputName] === stage.name,

  checkProcessorType: ({stageMatcherSchema, context: {processor}}) =>
    isUndefined(stageMatcherSchema.processorType) ||
    stageMatcherSchema.processorType === processor.type,

  checkValues: ({stageMatcherSchema, context: {stage}}) => {
    return every(stageMatcherSchema.values, ({type, hasPossibleValues}, valueName) => {
      return some(
        valueName === '*' ? values(stage.values) : [stage.values[valueName]],
        (stageValue) =>
          !isUndefined(stageValue) &&
          type === convertValueType(stageValue.type) && (
            isUndefined(hasPossibleValues) || hasPossibleValues === !isEmpty(stageValue.possible_values)
          )
      );
    });
  }
};

export const checkIfPatternMatches = (stageMatcherSchema: StageMatcherSchema, context: ProbePatternMatcherContext) =>
  overEvery(values(matchers))({stageMatcherSchema, context});
